import i18n from 'i18next';
import {Tabs} from 'platform/components';
import {useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import type {EntityResourceResolver} from '../../context/EntityResourceResolver';
import type {BulkOperationResponseBody} from '../../types/api/bulkOperations';
import type {BulkOperationsServices} from '../../types/BulkOperationsServices';
import {BulkOperationItems} from './BulkOperationItems';

type BulkOperationStatusTabsProps = {
  operation: BulkOperationResponseBody;
  services: BulkOperationsServices;
  entityResourceResolver?: EntityResourceResolver;
  successMessage?: string;
  testId?: string;
};

export function BulkOperationStatusTabs(props: BulkOperationStatusTabsProps) {
  const formatNumber = useFormatNumber();
  const operation = props.operation;
  const tabs = [
    {
      state: 'DONE',
      count: operation.doneCount,
      label: i18n.t('general.notifications.success'),
    },
    {
      state: 'SKIPPED',
      count: operation.skippedCount,
      label: i18n.t('entity.automationRule.detail.runResult.skipped'),
    },
    {
      state: 'FAILED',
      count: operation.failedCount,
      label: i18n.t('general.notifications.labels.failed'),
    },
  ] as const;
  const [activeState, setActiveState] = useState(() =>
    match({hasFailed: operation.failedCount > 0, hasSkipped: operation.skippedCount > 0})
      .with({hasFailed: true}, () => 'FAILED' as const)
      .with({hasSkipped: true}, () => 'SKIPPED' as const)
      .otherwise(() => 'DONE' as const)
  );
  const handleTabChange = (id: string) => {
    const tab = tabs.find((candidate) => candidate.state === id);
    if (isNotNil(tab)) {
      setActiveState(tab.state);
    }
  };

  return (
    <div
      role="group"
      aria-label={i18n.t('general.actions.bulkActions')}
      className="flex min-h-0 flex-col gap-4"
    >
      <Tabs
        activeTabId={activeState}
        onChange={handleTabChange}
        isFullWidth
        isLazy
        lazyBehavior="unmount"
        tabs={tabs.map((tab) => ({
          id: tab.state,
          title: `${tab.label} (${formatNumber(tab.count, 0)})`,
          content: (
            <div className="min-h-0 overflow-y-auto overscroll-contain">
              <BulkOperationItems
                key={`${operation.bulkOperationId}-${tab.state}`}
                services={props.services}
                entityResourceResolver={props.entityResourceResolver}
                bulkOperationId={operation.bulkOperationId}
                resourceId={operation.resourceId}
                state={tab.state}
                count={tab.count}
                title={tab.label}
                successMessage={props.successMessage}
                data-testid={props.testId}
              />
            </div>
          ),
        }))}
      />
    </div>
  );
}
